import { CalendarDays } from 'lucide-react'
import { de, enUS } from 'react-day-picker/locale'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Separator } from '@/components/shadcn/separator'
import { Button } from '@/components/shadcn/button'
import { Calendar } from '@/components/shadcn/calendar'

interface Props {
  /** Selected reference date as an ISO string. */
  value?: string
  onChange: (isoDate: string) => void
}

/**
 * Single-date picker (popover + calendar). The reference date selects the
 * action period whose ranking is shown — see `LeaderboardServiceModulePage`.
 * We store the start of day; the backend endpoint only compares `StartDate <= date`.
 */
export function LeaderboardDatePicker({ value, onChange }: Props) {
  const { t, i18n } = useTranslation('service-modules-leaderboard')
  const calendarLocale = i18n.resolvedLanguage?.startsWith('de') ? de : enUS
  const selected = value ? dayjs(value).toDate() : undefined

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-2">
          <CalendarDays className="h-4 w-4" />
          <span className="text-xs">{t('leaderboard.datePickerLabel')}</span>
          <Separator orientation="vertical" className="h-5" />
          <span className="text-xs font-medium">
            {value ? dayjs(value).format('DD.MM.YYYY') : 'DD.MM.YYYY'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={calendarLocale}
          selected={selected}
          defaultMonth={selected}
          onSelect={(date) => {
            if (date) onChange(dayjs(date).startOf('day').toISOString())
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  )
}
